import React, { useContext } from 'react';
import styled from 'styled-components';
import { tableHelper } from '@builttocreate/engine-utils';

import FieldDisplayTypes from '../../../constants/FieldDisplayTypes';
import FieldTableColumnTypes from '../../../constants/FieldTableColumnTypes';
import FieldTypes from '../../../constants/FieldTypes';

import { getFieldThemeObject } from '../../../theme/getThemeObject';
import ThemeContext from '../../../theme/ThemeContext';

import { getFieldSettingsConfigForField } from '../../../lib/settingsHelper';

import FieldSettingsBlockField from './FieldSettingsBlockField';
import FieldSettingsChartField from './FieldSettingsChartField';
import FieldSettingsCollectionSchema from './FieldSettingsCollectionSchema';
import FieldSettingsImageField from './FieldSettingsImageField';
import FieldSettingsMultiSelectField from './FieldSettingsMultiSelectField';
import FieldSettingsSignatureField from './FieldSettingsSignatureField';
import FieldSettingsTableCell from './FieldSettingsTableCell';
import FieldSettingsTableColumn from './FieldSettingsTableColumn';
import FieldSettingsTableField from './FieldSettingsTableField';
import FieldSettingsTextField from './FieldSettingsTextField';

const { getTargetColumnWithOperand } = tableHelper;

const StyledFieldWrapper = styled.div`
	margin-top: 5px;
`;

const FieldSettings = (props) => {
  const {
    _id,
    field,
    identifier,
    type,
    displayType,
    columnType,
    options,
    decorators,
    rowDecorators,
    onChange,
    onCapture,
    onDuplicate,
    onPositionDuplicate,
    onPositionChange,
    fieldDefaultStyles,
    config = {},
    mode,
    activeFieldSection,
    activeSchemaId,
    fieldMode, //Page Field Presentation
    useReadableIds
  } = props;

  /**
   * Step 1: Settings Configuration
   */
  const fieldSettingsConfig = getFieldSettingsConfigForField(props, config);
  const columnSettingsConfig = fieldSettingsConfig?.tableColumns;

  /**
  * Step 2: Theming and Field Styles
  */
  const theme = useContext(ThemeContext);
  const fieldTheme = getFieldThemeObject({
    mode,
    fieldPresentationMode: fieldMode,
    fieldPosition: props,
    fileStyles: fieldDefaultStyles,
    theme: theme?.field,
    sdkTheme: theme,
  });

  /**
   * Step 3: Set field props
   *
   * <!---------------NOTICE---------------------->
   *  Props should be passed after fieldTheme in the fieldProps object in order to pass the latest
   *   changes that were made via field settings.
   */

  const fieldProps = {
    fieldPositionId: _id,
    fieldId: field,
    fieldIdentifier: identifier,
    onDuplicate,
    onPositionChange,
    onPositionDuplicate,
    onOptionCreate: (fieldId, fieldIdentifier, fieldPositionId, data) => handleOptionCreate(fieldId, fieldIdentifier, fieldPositionId, options, data, onChange),
    onOptionChange: (fieldId, fieldIdentifier, fieldPositionId, optionId, data) => handleOptionUpdate(fieldId, fieldIdentifier, fieldPositionId, options, optionId, data, onChange),
    onOptionDelete: (fieldId, fieldIdentifier, fieldPositionId, optionId) => handleOptionUpdate(fieldId, fieldIdentifier, fieldPositionId, options, optionId, { deleted: true }, onChange),
    onDecoratorCreate: (fieldId, fieldIdentifier, fieldPositionId, data) => handleDecoratorCreate(fieldId, fieldIdentifier, fieldPositionId, decorators, data, onChange),
    onDecoratorChange: (fieldId, fieldIdentifier, fieldPositionId, decoratorId, data) => handleDecoratorUpdate(fieldId, fieldIdentifier, fieldPositionId, decorators, decoratorId, data, onChange),
    onDecoratorDelete: (fieldId, fieldIdentifier, fieldPositionId, decoratorId) => handleDecoratorUpdate(fieldId, fieldIdentifier, fieldPositionId, decorators, decoratorId, { deleted: true }, onChange),
    onRowDecoratorCreate: (fieldId, fieldIdentifier, fieldPositionId, data) => {
      const nextDecorators = rowDecorators ? [...rowDecorators] : [];
      nextDecorators.push({
        _id: data._id,
        icon: data.icon || '',
        label: data.label || '',
        action: data.action,
        color: data.color || '',
      });
      onChange(fieldId, fieldIdentifier, fieldPositionId, { rowDecorators: nextDecorators });
    },
    onRowDecoratorChange: (fieldId, fieldIdentifier, fieldPositionId, decoratorId, data) => {
      const nextDecorators = rowDecorators ? [...rowDecorators] : [];
      const index = nextDecorators.findIndex((d) => d._id === decoratorId);
      if (index === -1) return;
      nextDecorators[index] = { ...nextDecorators[index], ...data };
      onChange(fieldId, fieldIdentifier, fieldPositionId, { rowDecorators: nextDecorators });
    },
    onRowDecoratorDelete: (fieldId, fieldIdentifier, fieldPositionId, decoratorId) => {
      const nextDecorators = rowDecorators ? [...rowDecorators] : [];
      const index = nextDecorators.findIndex((d) => d._id === decoratorId);
      if (index === -1) return;
      nextDecorators.splice(index, 1);
      onChange(fieldId, fieldIdentifier, fieldPositionId, { rowDecorators: nextDecorators });
    },
    onCapture,
    fieldSection: activeFieldSection,
    useReadableIds,
    ...fieldTheme,
    ...props,
    config: fieldSettingsConfig || {}, //Important: Must go after "...props"
  };

  /**
   * Step 4: Render proper settings node
   */
  let fieldNode = null;

  if (type === FieldTypes.image || type === FieldTypes.file) {
    fieldNode = <FieldSettingsImageField {...fieldProps} />;
  } else if (type === FieldTypes.richText) {
    fieldNode = <FieldSettingsBlockField {...fieldProps} />;
  } else if (
    type === FieldTypes.text ||
    type === FieldTypes.rte ||
    type === FieldTypes.textarea ||
    type === FieldTypes.number ||
    type === FieldTypes.date ||
    type === FieldTypes.uniqueId ||
    type === FieldTypes.list
  ) {
    fieldNode = <FieldSettingsTextField {...fieldProps} />;
  } else if (type === FieldTypes.block) {
    fieldNode = <FieldSettingsBlockField {...fieldProps} />;
  } else if (type === FieldTypes.dropdown || type === FieldTypes.user) {
    fieldNode = (
      <StyledFieldWrapper>
        <FieldSettingsMultiSelectField {...fieldProps} />
      </StyledFieldWrapper>
    );
  } else if (type === FieldTypes.multiSelect) {
    fieldNode = (
      <StyledFieldWrapper>
        <FieldSettingsMultiSelectField {...fieldProps} />
      </StyledFieldWrapper>
    );
  } else if (type === FieldTypes.chart) {
    fieldNode = <FieldSettingsChartField {...fieldProps} />;
  } else if (type === FieldTypes.signature) {
    fieldNode = <FieldSettingsSignatureField {...fieldProps} />;
  }
  else if (activeSchemaId) {
    fieldNode = <FieldSettingsCollectionSchema {...fieldProps} />;
  }
  else if (type === FieldTypes.pricingTable) {
    fieldNode = <FieldSettingsTableField {...fieldProps} />;
  }
  else if ((type === FieldTypes.table || type === FieldTypes.collection) && (displayType === FieldDisplayTypes.original || displayType === FieldDisplayTypes.inputGroup || displayType === FieldDisplayTypes.rte)) {
    fieldNode = <FieldSettingsTableField {...fieldProps} />;
  }
  else if ((type === FieldTypes.table || type === FieldTypes.collection) && displayType !== FieldDisplayTypes.original && columnType === FieldTableColumnTypes.text) {

    const { tableColumns, column, tableColumnOrder } = props;

    //An individual table cell fieldPosition stores the associated target column._id under the "column" property.
    const targetColumnId = column;
    const targetColumn = getTargetColumnWithOperand(tableColumnOrder, tableColumns, targetColumnId);

    fieldNode = (
      <FieldSettingsTableCell
        {...fieldProps}
        columnIdentifier={targetColumn.identifier}
        columnType={columnType}
        config={fieldSettingsConfig}
        columnConfig={columnSettingsConfig}
        columnOperand={targetColumn.operand}
        metadata={targetColumn.metadata}
        rows={fieldProps.value}
        onColumnChange={(fieldId, fieldIdentifier, fieldPositionId, data) => handleColumnUpdate(fieldId, fieldIdentifier, fieldPositionId, tableColumns, targetColumnId, data, onChange)}
      />
    );

  }
  else if ((type === FieldTypes.table || type === FieldTypes.collection) && displayType !== FieldDisplayTypes.original && columnType === FieldTableColumnTypes.barcode) {

    const { tableColumns, column, tableColumnOrder } = props;

    //An individual table cell fieldPosition stores the associated target column._id under the "column" property.
    const targetColumnId = column;
    const targetColumn = getTargetColumnWithOperand(tableColumnOrder, tableColumns, targetColumnId);

    fieldNode = (
      <FieldSettingsTableCell
        {...fieldProps}
        columnIdentifier={targetColumn.identifier}
        columnType={columnType}
        config={fieldSettingsConfig}
        columnConfig={columnSettingsConfig}
        columnOperand={targetColumn.operand}
        metadata={targetColumn.metadata}
        rows={fieldProps.value}
        onColumnChange={(fieldId, fieldIdentifier, fieldPositionId, data) => handleColumnUpdate(fieldId, fieldIdentifier, fieldPositionId, tableColumns, targetColumnId, data, onChange)}
      />
    );

  }
  else if ((type === FieldTypes.table || type === FieldTypes.collection) && displayType !== FieldDisplayTypes.original && columnType === FieldTableColumnTypes.signature) {

    const { tableColumns, column, tableColumnOrder } = props;

    //An individual table cell fieldPosition stores the associated target column._id under the "column" property.
    const targetColumnId = column;
    const targetColumn = getTargetColumnWithOperand(
      tableColumnOrder,
      tableColumns,
      targetColumnId
    );

    fieldNode = (
      <FieldSettingsTableCell
        {...fieldProps}
        columnIdentifier={targetColumn.identifier}
        columnType={columnType}
        config={fieldSettingsConfig}
        columnConfig={columnSettingsConfig}
        columnOperand={targetColumn.operand}
        metadata={targetColumn.metadata}
        rows={fieldProps.value}
        onColumnChange={(fieldId, fieldIdentifier, fieldPositionId, data) => handleColumnUpdate(
          fieldId,
          fieldIdentifier,
          fieldPositionId,
          tableColumns,
          targetColumnId,
          data,
          onChange
        )}
      />
    );
  } else if (
    type === FieldTypes.table &&
    displayType !== FieldDisplayTypes.original &&
    columnType === FieldTableColumnTypes.image
  ) {
    const { tableColumns, column, tableColumnOrder } = props;

    //An individual  table cell fieldPosition stores the associated target column._id under the "column" property.
    const targetColumnId = column;
    const targetColumn = getTargetColumnWithOperand(
      tableColumnOrder,
      tableColumns,
      targetColumnId
    );

    fieldNode = (
      <FieldSettingsTableCell
        {...fieldProps}
        columnIdentifier={targetColumn.identifier}
        columnType={columnType}
        config={fieldSettingsConfig}
        columnConfig={columnSettingsConfig}
        columnOperand={targetColumn.operand}
        metadata={targetColumn.metadata}
        rows={fieldProps.value}
        maxImageHeight={targetColumn.maxImageHeight}
        maxImageWidth={targetColumn.maxImageWidth}
        onColumnChange={(fieldId, fieldIdentifier, fieldPositionId, data) => handleColumnUpdate(
          fieldId,
          fieldIdentifier,
          fieldPositionId,
          tableColumns,
          targetColumnId,
          data,
          onChange
        )}
      />
    );

  }
  else if (type === FieldTypes.table && displayType !== FieldDisplayTypes.original && (columnType === FieldTableColumnTypes.dropdown || columnType === FieldTableColumnTypes.multiSelect)) {

    const { tableColumns, column, tableColumnOrder } = props;

    //An individual table cell fieldPosition stores the associated target column._id under the "column" property.
    const targetColumnId = column;
    const targetColumn = getTargetColumnWithOperand(
      tableColumnOrder,
      tableColumns,
      targetColumnId
    );

    fieldNode = (
      <StyledFieldWrapper>
        <FieldSettingsTableCell
          {...fieldProps}
          disableDragAndDrop={true}
          columnIdentifier={targetColumn.identifier}
          columnType={columnType}
          config={fieldSettingsConfig}
          columnConfig={columnSettingsConfig}
          rows={fieldProps.value}
          columnOperand={targetColumn.operand}
          options={targetColumn.options}
          metadata={targetColumn.metadata}
          onColumnChange={(fieldId, fieldIdentifier, fieldPositionId, data) => handleColumnUpdate(
            fieldId,
            fieldIdentifier,
            fieldPositionId,
            tableColumns,
            targetColumnId,
            data,
            onChange
          )}
          onOptionCreate={(fieldId, fieldIdentifier, fieldPositionId, data) => handleColumnOptionCreate(
            fieldId,
            fieldIdentifier,
            fieldPositionId,
            tableColumns,
            targetColumnId,
            data,
            onChange
          )}
          onOptionChange={(
            fieldId,
            fieldIdentifier,
            fieldPositionId,
            optionId,
            data
          ) => handleColumnOptionUpdate(
            fieldId,
            fieldIdentifier,
            fieldPositionId,
            tableColumns,
            targetColumnId,
            optionId,
            data,
            onChange
          )}
          onOptionDelete={(
            fieldId,
            fieldIdentifier,
            fieldPositionId,
            optionId
          ) => handleColumnOptionUpdate(
            fieldId,
            fieldIdentifier,
            fieldPositionId,
            tableColumns,
            targetColumnId,
            optionId,
            { deleted: true },
            onChange
          )}
        />
      </StyledFieldWrapper>
    );
  }

  else if (type === FieldTypes.table && displayType !== FieldDisplayTypes.original && columnType === FieldTableColumnTypes.number) {

    const { tableColumns, column, tableColumnOrder } = props;

    //An individual table cell fieldPosition stores the associated target column._id under the "column" property.
    const targetColumnId = column;
    const targetColumn = getTargetColumnWithOperand(tableColumnOrder, tableColumns, targetColumnId);

    fieldNode = (
      <FieldSettingsTableCell
        {...fieldProps}
        columnIdentifier={targetColumn.identifier}
        columnType={columnType}
        config={fieldSettingsConfig}
        columnConfig={columnSettingsConfig}
        columnOperand={targetColumn.operand}
        metadata={targetColumn.metadata}
        rows={fieldProps.value}
        onColumnChange={(fieldId, fieldIdentifier, fieldPositionId, data) => handleColumnUpdate(fieldId, fieldIdentifier, fieldPositionId, tableColumns, targetColumnId, data, onChange)}
      />
    );

  }
  else if (type === FieldTypes.table && displayType !== FieldDisplayTypes.original && columnType === FieldTableColumnTypes.date) {

    const { tableColumns, column, tableColumnOrder } = props;

    //An individual table cell fieldPosition stores the associated target column._id under the "column" property.
    const targetColumnId = column;
    const targetColumn = getTargetColumnWithOperand(tableColumnOrder, tableColumns, targetColumnId);

    if (targetColumn && targetColumn.format) fieldProps.format = targetColumn.format;

    fieldNode = (
      <FieldSettingsTableCell
        {...fieldProps}
        columnIdentifier={targetColumn.identifier}
        columnType={columnType}
        config={fieldSettingsConfig}
        columnConfig={columnSettingsConfig}
        columnOperand={targetColumn.operand}
        metadata={targetColumn.metadata}
        rows={fieldProps.value}
        onColumnChange={(fieldId, fieldIdentifier, fieldPositionId, data) => handleColumnUpdate(fieldId, fieldIdentifier, fieldPositionId, tableColumns, targetColumnId, data, onChange)}
        onPositionChange={onPositionChange}
      />
    );

  }

  return fieldNode;
};

const getTableColumnDetails = (columnId, tableId, schema) => {
  // Extract table columns from schema
  const tableColumns = schema[tableId]?.tableColumns || [];

  // Find the target column
  const columnIndex = tableColumns.findIndex((col) => col._id === columnId);
  if (columnIndex === -1) return { tableColumns, targetColumn: null }; // Column not found

  const column = tableColumns[columnIndex];

  const targetColumn = {
    ...column, // Spread existing column properties
    identifier: column.identifier || '', // Assign empty string if identifier is missing
    operand: String.fromCharCode(65 + columnIndex) // Convert index to alphabetical (A, B, C, ...)
  };

  return { tableColumns, targetColumn };
};

export const TableColumnSettings = (props) => {

  const {
    _id,
    field,
    identifier,
    tableColumns,
    tableColumnOrder,
    columnId,
    tableId,
    type,
    schema,
    onChange,
    onPositionChange,
    onColumnIdChange,
    config,
    onFieldColumnDelete,
    onCollectionChange,
    onCapture,
    useReadableIds
  } = props;

  // Get column details from schema
  const targetCollection = (type !== FieldTypes.table) ? getTableColumnDetails(columnId, tableId, schema) : {};

  // Determine the correct target column
  // Handle the case where column might not exist yet (during ID change)
  let targetColumn = {};

  if (type === FieldTypes.table) {
    const columnExists = tableColumns && tableColumns.find(col => col._id === columnId);
    if (columnExists) {
      targetColumn = getTargetColumnWithOperand(tableColumnOrder, tableColumns, columnId);
    } else {
      // Column doesn't exist yet (e.g., ID is being changed), return null to avoid errors
      return null;
    }
  } else {
    targetColumn = targetCollection.targetColumn || {};
    // For collection fields, also check if the column exists
    if (!targetColumn || !targetColumn._id) {
      return null;
    }
  }

  // Extract column settings configuration
  const fieldSettingsConfig = config[identifier] || config[`${type}Field`] || config[type] || config?.field || config?.[_id] || {};

  const columnSettingsConfig = fieldSettingsConfig?.tableColumns || {};

  const fieldProps = {
    ...props,
    ...targetColumn,

    fieldId: field,
    fieldIdentifier: identifier,
    fieldPositionId: _id,
    fieldType: type,

    schema,
    tableId,
    tableColumns: targetCollection.tableColumns || tableColumns,
    config: columnSettingsConfig,
    displayType: FieldDisplayTypes.original,
    columnType: targetColumn.type,
    columnOperand: targetColumn.operand || '',
    value: targetColumn.value || '',
    onChange,
    onPositionChange,
    onOptionCreate: (fieldId, fieldIdentifier, fieldPositionId, data) => handleColumnOptionCreate(fieldId, fieldIdentifier, fieldPositionId, tableColumns, columnId, data, onChange),
    onOptionChange: (fieldId, fieldIdentifier, fieldPositionId, optionId, data) => handleColumnOptionUpdate(fieldId, fieldIdentifier, fieldPositionId, tableColumns, columnId, optionId, data, onChange),
    onOptionDelete: (fieldId, fieldIdentifier, fieldPositionId, optionId) => handleColumnOptionUpdate(fieldId, fieldIdentifier, fieldPositionId, tableColumns, columnId, optionId, { deleted: true }, onChange),
    onColumnIdChange: (activeColumnId) => onColumnIdChange(activeColumnId),
    onCapture,
    useReadableIds,
    required: targetColumn.required,
    /**
     * onFieldColumnDelete is used for
     * 1.Removing the active column settings selection and hiding the column settings panel.
     * 2.Deleting the column
     */
    onFieldColumnDelete,
    onCollectionChange,
    required: targetColumn ? targetColumn.required : false,
  };

  return (
    <StyledFieldWrapper>
      <FieldSettingsTableColumn {...fieldProps} />
    </StyledFieldWrapper>
  );
};

export const handleColumnUpdate = (
  fieldId,
  fieldIdentifier,
  fieldPositionId,
  columns,
  columnId,
  data,
  onChange
) => {
  const nextColumns = [...columns];
  const columnIndex = nextColumns.findIndex((col) => col._id === columnId);
  const column = { ...nextColumns[columnIndex] };

  const payload = {};

  if (data && data.format) {

    const fieldPositionTableColumns = {};

    fieldPositionTableColumns[columnId] = {
      format: data.format
    };
    //Adding the new dateColumn format to the field.tableColumns
    nextColumns[columnIndex] = { ...nextColumns[columnIndex], fieldPositionTableColumns };

  }

  else {

    const nextColumn = { ...column };
    if (column.options) nextColumn.options = [...column.options];

    nextColumns[columnIndex] = {
      ...nextColumn,
      ...data
    };

  }

  payload.tableColumns = nextColumns;

  onChange(fieldId, fieldIdentifier, fieldPositionId, payload, columnId);

};

export const handleColumnOptionCreate = (
  fieldId,
  fieldIdentifier,
  fieldPositionId,
  columns,
  columnId,
  data,
  onChange
) => {
  const nextColumns = [...columns];
  const columnIndex = nextColumns.findIndex((col) => col._id === columnId);
  const column = { ...nextColumns[columnIndex] };

  /**
   * If the column.options is undefined, create an empty array
   * to avoid errors when pushing new options.
   */
  if (!column.options) {
    column.options = [];
  }

  nextColumns[columnIndex] = {
    ...column,
    options: [...column.options],
  };

  const isMultiSelect = nextColumns[columnIndex].type === FieldTableColumnTypes.multiSelect;

  nextColumns[columnIndex].options.push({
    _id: data._id,
    value: data.value || 'New Option',
    metadata: data.metadata || {},
    ...(isMultiSelect ? { width: data.width} : {}),
    deleted: false,
    ...(isMultiSelect ? { styles: data.styles || {} } : {})
  });

  onChange(fieldId, fieldIdentifier, fieldPositionId, { tableColumns: nextColumns }, columnId);
};

export const handleColumnOptionUpdate = (
  fieldId,
  fieldIdentifier,
  fieldPositionId,
  columns,
  columnId,
  optionId,
  data,
  onChange
) => {
  const nextColumns = [...columns];
  const columnIndex = nextColumns.findIndex((col) => col._id === columnId);
  const column = { ...nextColumns[columnIndex] };

  nextColumns[columnIndex] = {
    ...column,
    options: [...column.options],
  };

  const optionIndex = column.options.findIndex(
    (option) => option._id === optionId
  );

  nextColumns[columnIndex].options[optionIndex] = {
    ...nextColumns[columnIndex].options[optionIndex],
    ...data,
  };

  onChange(fieldId, fieldIdentifier, fieldPositionId, { tableColumns: nextColumns }, columnId);
};

export const handleOptionCreate = (
  fieldId,
  fieldIdentifier,
  fieldPositionId,
  options,
  data,
  onChange
) => {
  const nextOptions = [...options];

  const newOption = {
    _id: data._id,
    value: data.value || 'Option',
    width: data.width || 100,
    deleted: false,
  };

  nextOptions.push(newOption);

  onChange(fieldId, fieldIdentifier, fieldPositionId, { options: nextOptions });
};

export const handleOptionUpdate = (
  fieldId,
  fieldIdentifier,
  fieldPositionId,
  options,
  optionId,
  data,
  onChange
) => {
  const nextOptions = [...options];

  const optionIndex = nextOptions.findIndex((option) => option._id === optionId);

  const nextOption = { ...nextOptions[optionIndex], ...data };

  nextOptions[optionIndex] = nextOption;

  onChange(fieldId, fieldIdentifier, fieldPositionId, { options: nextOptions });
};

export const handleDecoratorCreate = (
  fieldId,
  fieldIdentifier,
  fieldPositionId,
  decorators,
  data,
  onChange
) => {
  const nextDecorators = decorators ? [...decorators] : [];

  const newDecorator = {
    _id: data._id,
    icon: data.icon || '',
    label: data.label || '',
    action: data.action,
    color: data.color || '',
  };

  nextDecorators.push(newDecorator);

  onChange(fieldId, fieldIdentifier, fieldPositionId, { decorators: nextDecorators });
};

export const handleDecoratorUpdate = (
  fieldId,
  fieldIdentifier,
  fieldPositionId,
  decorators,
  decoratorId,
  data,
  onChange
) => {
  const nextDecorators = decorators ? [...decorators] : [];

  const decoratorIndex = nextDecorators.findIndex((d) => d._id === decoratorId);

  if (decoratorIndex === -1) return;

  if (data.deleted) {
    nextDecorators.splice(decoratorIndex, 1);
  } else {
    const nextDecorator = { ...nextDecorators[decoratorIndex], ...data };
    nextDecorators[decoratorIndex] = nextDecorator;
  }

  onChange(fieldId, fieldIdentifier, fieldPositionId, { decorators: nextDecorators });
};

export default FieldSettings;
